export function isAbsoluteHref(href: string) {
   return /^(?:[a-z]+:)?\/\//i.test(href);
}

export function isRelativeHref(href: string) {
   return !isAbsoluteHref(href);
}

/**
 * Drop the origin from a url, keeping path + query + hash (so a ?variant=
 * deep-link survives). Mirrors the app's toRelativeUrl, but lives here so shared
 * components (which can't import app helpers) can relativize an index url.
 */
export function toRelativeHref(href: string): string {
   const url = new URL(href, 'http://dummy-domain.invalid');
   return url.pathname + url.search + url.hash;
}

export function isIFixitUSUrl(url: URL) {
   return isIfixitUrl(url) || isCominorUrl(url);
}

export function isIfixitUrl(url: URL) {
   return /^(?:www\.)?ifixit\.com$/.test(url.hostname);
}

export function isCominorUrl(url: URL) {
   return /^(?:[^.]+\.)?cominor\.com$/.test(url.hostname);
}

export function isNextJsPath(path: string) {
   return (
      /^\/cart\/?$/.test(path) ||
      /^\/Store(?:\/.*)?$/.test(path) ||
      /^\/Support(?:\/.*)?$/.test(path) ||
      /^\/Parts(?:\/.*)?$/.test(path) ||
      /^\/Tools(?:\/.*)?$/.test(path) ||
      /^\/go\/[^/]+\/?$/.test(path) ||
      /^\/Shop\/[^/]+\/?$/.test(path) ||
      /^\/products\/[^/]+\/?$/.test(path) ||
      /^\/Troubleshooting\/[^/]+\/?$/.test(path) ||
      /^\/Troubleshooting(?:\/[^/]+){3}\/?$/.test(path) ||
      /^\/pro\/?(?:\?.*)?$/.test(path) ||
      /^\/go\/workbench(?:\/.*)?$/.test(path) ||
      /^\/fixhub\/console\/?$/.test(path)
   );
}

export function getCookieDomain(url?: URL | Location): string | undefined {
   return getCookieDomainFromHost(url?.hostname || '');
}

export function getCookieDomainFromHost(hostname: string): string | undefined {
   const host = stripPort(hostname);
   // IP addresses (e.g. 0.0.0.0, the Next.js standalone server default) and
   // single-label hosts (localhost) have no registrable parent domain, and
   // browsers reject Domain attributes like `.0.0` or `.localhost`, silently
   // dropping the cookie. Omit Domain so the cookie is set host-only.
   if (isIpAddress(host) || !host.includes('.')) return undefined;
   const currentDomain = host.split('.').splice(-2).join('.');
   // vercel.app is a public suffix, so we cannot use it as a valid cookie domain
   // https://vercel.com/guides/can-i-set-a-cookie-from-my-vercel-project-subdomain-to-vercel-app
   return currentDomain === 'vercel.app' ? host : '.' + currentDomain;
}

// URL.hostname never carries a port, but callers also pass the
// x-ifixit-forwarded-host header, which is Host-header shaped and may
// include one (e.g. `www.ifixit.com:443`, `[::1]:3000`).
function stripPort(host: string): string {
   const bracketedIpv6 = host.match(/^\[(.+)\](?::\d+)?$/);
   if (bracketedIpv6) return bracketedIpv6[1];
   // With brackets ruled out, a single colon before trailing digits is a
   // port; bare IPv6 has at least two colons and never matches.
   const hostPort = host.match(/^([^:]+):\d+$/);
   return hostPort ? hostPort[1] : host;
}

function isIpAddress(hostname: string): boolean {
   // After stripPort, any remaining colon means IPv6; IPv4 is dotted digits.
   return hostname.includes(':') || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
}

/**
 * The web page an `ifixitmobile://` app link carries in its `url` param, made
 * absolute against `origin`, or null when it carries none. Only https is
 * accepted, so a notification can't send the browser to a `javascript:` url.
 */
export function appLinkWebUrl(appLink: string, origin: string): string | null {
   const queryStart = appLink.indexOf('?');
   if (queryStart === -1) {
      return null;
   }
   const candidate = new URLSearchParams(appLink.slice(queryStart + 1)).get('url');
   if (!candidate) {
      return null;
   }
   try {
      const parsed = new URL(candidate, origin);
      return parsed.protocol === 'https:' ? parsed.href : null;
   } catch {
      return null;
   }
}
